import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import AppHeader from '../components/AppHeader';
import AlertModal from '../components/AlertModal';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscriptionService';
import razorpayService from '../services/razorpayService';

const formatCurrency = (amount) => `₹${amount.toLocaleString()}`;

const SubscriptionPage = () => {
  const { selectedBranch, restaurants, allBranches, listenAllMode, requestBranchSelect } = useBranch();
  const { user } = useAuth();
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [currentUsage, setCurrentUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertActions, setAlertActions] = useState([]);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
  const [topUpOrders, setTopUpOrders] = useState('');
  const [topUpError, setTopUpError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [showUpgradeBreakdown, setShowUpgradeBreakdown] = useState(false);
  const [upgradeBreakdown, setUpgradeBreakdown] = useState(null);
  const upgradeConfirmRef = useRef({ resolve: null, reject: null });
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [paymentConfirmData, setPaymentConfirmData] = useState({ amount: 0, description: '' });
  const paymentConfirmRef = useRef({ resolve: null, reject: null });

  useEffect(() => {
    if (listenAllMode && (restaurants?.length > 1 || allBranches?.length > 1)) {
      requestBranchSelect();
    }
  }, [listenAllMode, restaurants?.length, allBranches?.length, requestBranchSelect]);

  const showAlert = (title, message, actions) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertActions(actions || [{ text: 'OK', variant: 'primary', onPress: () => setAlertVisible(false) }]);
    setAlertVisible(true);
  };

  const confirmAlert = (title, message, confirmText = 'OK', cancelText = 'Cancel') => {
    return new Promise((resolve, reject) => {
      setAlertTitle(title);
      setAlertMessage(message);
      setAlertActions([
        { text: cancelText, onPress: () => { setAlertVisible(false); reject(new Error('User cancelled')); } },
        { text: confirmText, variant: 'primary', onPress: () => { setAlertVisible(false); resolve(); } },
      ]);
      setAlertVisible(true);
    });
  };

  const fetchSubscriptionData = async () => {
    if (!selectedBranch?._id) {
      setCurrentSubscription(null);
      setCurrentUsage(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    setLoading(true);
    try {
      const plansResponse = await subscriptionService.getAllSubscriptions();
      const plansWithTiers = plansResponse.subscriptions.map(plan => {
        if (!plan.pricingTiers || plan.pricingTiers.length === 0) {
          return {
            ...plan,
            pricingTiers: [{
              _id: `default-tier-${plan._id}`,
              minOrders: 0,
              maxOrders: plan.maxOrders || 1000,
              price: plan.price || 0,
              billingPeriod: 'monthly'
            }]
          };
        }
        return {
          ...plan,
          pricingTiers: [...plan.pricingTiers].sort((a, b) => a.price - b.price)
        };
      });
      setSubscriptionPlans(plansWithTiers);

      try {
        const statusResponse = await subscriptionService.getSubscriptionStatus(selectedBranch._id);
        if (statusResponse && statusResponse.length > 0) {
          const subscription = statusResponse[0];
          if (subscription.subscription) {
            if (!subscription.subscription.pricingTiers || subscription.subscription.pricingTiers.length === 0) {
              const fullPlan = plansWithTiers.find(p => p._id === subscription.subscription._id);
              if (fullPlan) {
                subscription.subscription = fullPlan;
              }
            }
          }
          setCurrentSubscription(subscription);

          const usageData = {
            orderCount: subscription.orderCount || 0,
            maxOrders: subscription.maxOrders || 0,
            remainingOrders: Math.max(0, (subscription.maxOrders || 0) - (subscription.orderCount || 0)),
            usagePercentage: subscription.maxOrders
              ? Math.round(((subscription.orderCount || 0) / subscription.maxOrders) * 100)
              : 0
          };
          setCurrentUsage(usageData);
        } else {
          setCurrentSubscription(null);
          setCurrentUsage(null);
        }
      } catch (statusError) {
        
        setCurrentSubscription(null);
        setCurrentUsage(null);
      }
    } catch (error) {
      
      showAlert('Error', 'Failed to load subscription data. Please try again later.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      fetchSubscriptionData();
    }
  }, [selectedBranch]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSubscriptionData();
  };

  const isDowngradePlan = (plan) => {
    if (!currentSubscription?.subscription?._id) return false;
    const currentPlanIdx = subscriptionPlans.findIndex(p => p._id === currentSubscription.subscription._id);
    const newPlanIdx = subscriptionPlans.findIndex(p => p._id === plan._id);
    return currentPlanIdx !== -1 && newPlanIdx < currentPlanIdx;
  };

  const handleSelectPlan = (plan) => {
    if (isDowngradePlan(plan)) {
      showAlert('Not Allowed', 'Downgrading subscription plans is not permitted.');
      return;
    }
    setSelectedPlan(plan);
    setTierDialogOpen(true);
  };

  const handleTierSelection = async (selectedTier) => {
    if (!selectedPlan || !selectedBranch?._id) {
      showAlert('Error', 'Invalid selection');
      return;
    }
    if (!selectedTier || !selectedTier._id) {
      showAlert('Error', 'Invalid tier selection');
      return;
    }
    const remaining = currentUsage?.remainingOrders || 0;
    const tierMax = selectedTier.maxOrders === 10000 ? Number.MAX_SAFE_INTEGER : selectedTier.maxOrders;
    if (remaining > tierMax) {
      showAlert('Not Allowed', 'Your remaining orders exceed the selected tier limit. Please choose a higher tier.');
      return;
    }
    try {
      let paymentAmount = selectedTier.price;
      let actionType = 'purchase';
      if (currentSubscription && currentSubscription.status === 'active') {
        if (currentSubscription.subscription?._id === selectedPlan._id) actionType = 'renew';
        else actionType = 'upgrade';
      }
      const actionText = actionType === 'renew' ? 'renew' : actionType === 'upgrade' ? 'upgrade to' : 'purchase';
      try {
        await new Promise((resolve, reject) => {
          const desc = `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} ${selectedPlan.name} subscription - ${selectedTier.minOrders}-${selectedTier.maxOrders === 10000 ? 'Unlimited' : selectedTier.maxOrders} orders`;
          setPaymentConfirmData({ amount: paymentAmount, description: desc });
          paymentConfirmRef.current = { resolve, reject };
          setShowPaymentConfirm(true);
        });
      } catch (err) {
        return;
      }

      try {
        setPaymentLoading(true);
        let razorpayOrderId;
        let costCalculation;
        const tierData = {
          tierId: selectedTier._id,
          maxOrders: selectedTier.maxOrders || 1000,
          minOrders: selectedTier.minOrders || 0,
          durationInDays: selectedPlan.durationInDays || 30,
        };
        if (actionType === 'renew') {
          try {
            const costResponse = await subscriptionService.calculateRenewalCost(selectedBranch._id, selectedTier._id);
            const calc = costResponse.data;
            const breakdownMessage = `Renewal Cost Breakdown:\n\n` +
              `Current Plan: ${calc.currentPlan.name}\n` +
              `Remaining Orders: ${calc.currentPlan.remainingOrders}\n` +
              `Credit Value: ₹${calc.remainingOrdersCredit.toFixed(2)}\n\n` +
              `New Tier: ${selectedPlan.name}\n` +
              `Original Cost: ₹${calc.newTierCost}\n\n` +
              `Credit Applied: ₹${calc.remainingOrdersCredit.toFixed(2)}\n` +
              `Final Amount to Pay: ₹${calc.finalRenewalCost.toFixed(2)}`;
            await confirmAlert('Renewal Cost Breakdown', breakdownMessage, 'Proceed with Payment', 'Cancel');
            const resp = await subscriptionService.renewSubscription(selectedBranch._id, calc.finalRenewalCost, tierData);
            razorpayOrderId = resp.razorpayOrderId;
            paymentAmount = calc.finalRenewalCost;
          } catch (err) {
            setPaymentLoading(false);
            setTierDialogOpen(false);
            return;
          }
        } else if (actionType === 'upgrade') {
          const costResponse = await subscriptionService.calculateUpgradeCost(selectedBranch._id, selectedPlan._id, selectedTier._id);
          costCalculation = costResponse.data;
          const finalUpgradeCost = costCalculation.calculation.finalUpgradeCost;
          try {
            await new Promise((resolve, reject) => {
              setUpgradeBreakdown({
                currentPlanName: costCalculation.currentPlan.name,
                remainingOrders: costCalculation.currentPlan.remainingOrders,
                creditValue: costCalculation.calculation.remainingOrdersCredit,
                newPlanName: selectedPlan.name,
                originalCost: costCalculation.newPlan.cost,
                finalAmount: finalUpgradeCost,
              });
              upgradeConfirmRef.current = { resolve, reject };
              setShowUpgradeBreakdown(true);
            });
          } catch (err) {
            setPaymentLoading(false);
            setTierDialogOpen(false);
            return;
          }
          const resp = await subscriptionService.upgradeSubscription(selectedBranch._id, selectedPlan._id, finalUpgradeCost, tierData);
          razorpayOrderId = resp.razorpayOrderId;
          paymentAmount = finalUpgradeCost;
        } else {
          const resp = await subscriptionService.purchaseSubscription(selectedBranch._id, selectedPlan._id, selectedTier.price, tierData);
          razorpayOrderId = resp.razorpayOrderId;
        }
        let razorpayOptions;
        const userDetails = { name: user?.name || '', email: user?.email || '', contact: user?.phoneNumber || '' };
        if (actionType === 'renew') {
          razorpayOptions = razorpayService.createSubscriptionRenewalOptions(razorpayOrderId, paymentAmount, `${selectedPlan.name} (${selectedTier.minOrders}-${selectedTier.maxOrders === 10000 ? 'Unlimited' : selectedTier.maxOrders} orders)`, userDetails);
        } else if (actionType === 'upgrade') {
          razorpayOptions = razorpayService.createSubscriptionUpgradeOptions(razorpayOrderId, paymentAmount, `${selectedPlan.name} (${selectedTier.minOrders}-${selectedTier.maxOrders === 10000 ? 'Unlimited' : selectedTier.maxOrders} orders)`, userDetails);
        } else {
          razorpayOptions = razorpayService.createSubscriptionPurchaseOptions(razorpayOrderId, paymentAmount, `${selectedPlan.name} (${selectedTier.minOrders}-${selectedTier.maxOrders === 10000 ? 'Unlimited' : selectedTier.maxOrders} orders)`, userDetails);
        }
        const paymentResult = await razorpayService.openCheckout(razorpayOptions);
        setTierDialogOpen(false);
        if (paymentResult.success && paymentResult.paymentId && paymentResult.orderId && paymentResult.signature) {
          const verificationData = {
            razorpayPaymentId: paymentResult.paymentId,
            razorpayOrderId: paymentResult.orderId,
            razorpaySignature: paymentResult.signature,
            razorpay_order_id: paymentResult.orderId,
            razorpay_payment_id: paymentResult.paymentId,
            razorpay_signature: paymentResult.signature,
          };
          try {
            let verifyResponse;
            const restaurantData = { ownerName: user?.name || '', ownerEmail: user?.email || '' };
            if (actionType === 'renew') {
              verifyResponse = await subscriptionService.verifyRenewSubscription(verificationData, selectedBranch._id, selectedPlan._id, selectedTier.price, restaurantData);
            } else if (actionType === 'upgrade') {
              verifyResponse = await subscriptionService.verifyUpgradeSubscription(verificationData, selectedBranch._id, selectedPlan._id, costCalculation?.calculation?.finalUpgradeCost || selectedTier.price, restaurantData);
            } else {
              verifyResponse = await subscriptionService.verifyPurchaseSubscription(verificationData, selectedBranch._id, selectedPlan._id, selectedTier.price, restaurantData);
            }
            if (verifyResponse.success) {
              showAlert('Success', verifyResponse.message);
              fetchSubscriptionData();
              setSelectedPlan(null);
            } else {
              showAlert('Error', verifyResponse.message || 'Payment verification failed');
            }
          } catch (verifyError) {
            if (verifyError?.response?.status === 500 && (verifyError?.response?.data?.message?.includes('Invalid Date') || verifyError?.response?.data?.message?.includes('endDate') || verifyError?.response?.data?.message?.includes('NaN'))) {
              showAlert('Payment Processed', 'Your payment was processed successfully, but there was an issue updating your subscription. The subscription should be activated shortly.', [
                { text: 'OK', variant: 'primary', onPress: () => { setAlertVisible(false); setTimeout(() => { fetchSubscriptionData(); setSelectedPlan(null); }, 5000); } },
              ]);
            } else {
              showAlert('Backend Error', 'The payment was processed, but there was a problem updating your subscription. Please contact support with your payment ID.', [
                { text: 'OK', variant: 'primary', onPress: () => { setAlertVisible(false); fetchSubscriptionData(); setSelectedPlan(null); } },
              ]);
            }
          }
        } else {
          showAlert('Payment Failed', paymentResult.error || 'Payment was not successful or missing payment details.');
        }
      } catch (error) {
        setTierDialogOpen(false);
        showAlert('Error', error?.message || 'Failed to process subscription');
      } finally {
        setPaymentLoading(false);
      }
    } catch (error) {
      showAlert('Error', 'Failed to select plan');
    }
  };

  const handleTopUp = () => {
    setTopUpDialogOpen(true);
  };
  const perOrderAmount = 10;
  const totalTopUpAmount = (parseInt(topUpOrders, 10) || 0) * perOrderAmount;
  const validateTopUpInput = () => {
    const n = parseInt(topUpOrders, 10);
    if (!topUpOrders || isNaN(n)) return 'Please enter a valid number of orders';
    if (n <= 0) return 'Order count must be greater than 0';
    if (n > 100000) return 'Order count is too high';
    return '';
  };
  const submitTopUp = async () => {
    const err = validateTopUpInput();
    if (err) {
      setTopUpError(err);
      return;
    }
    setTopUpError('');
    try {
      setTopUpLoading(true);
      const orders = parseInt(topUpOrders, 10);
      try {
        await new Promise((resolve, reject) => {
          setPaymentConfirmData({ amount: totalTopUpAmount, description: `Top-up ${orders} orders` });
          paymentConfirmRef.current = { resolve, reject };
          setShowPaymentConfirm(true);
        });
      } catch (e) {
        setTopUpLoading(false);
        return;
      }

      try {
        const response = await subscriptionService.topUpOrderCount(selectedBranch._id, orders);
        const razorpayOrderId = response.razorpayOrderId;
        const options = razorpayService.createTopUpOptions(razorpayOrderId, totalTopUpAmount, orders, { name: user?.name || '', email: user?.email || '' });
        const paymentResult = await razorpayService.openCheckout(options);
        if (paymentResult.success && paymentResult.paymentId && paymentResult.orderId && paymentResult.signature) {
          const verificationData = razorpayService.handlePaymentSuccess(paymentResult.paymentId, paymentResult.orderId, paymentResult.signature);
          const restaurantData = { ownerName: user?.name || '', ownerEmail: user?.email || '' };
          const verifyResponse = await subscriptionService.verifyTopUpPayment(verificationData, selectedBranch._id, totalTopUpAmount, restaurantData);
          if (verifyResponse.success) {
            showAlert('Success', verifyResponse.message);
            setTopUpOrders('');
            setTopUpDialogOpen(false);
            fetchSubscriptionData();
          } else {
            showAlert('Error', verifyResponse.message || 'Payment verification failed');
          }
        } else {
          showAlert('Payment Failed', paymentResult.error || 'Unknown error');
        }
      } catch (error) {
        showAlert('Error', 'Failed to process top-up');
      } finally {
        setTopUpLoading(false);
      }
    } catch (error) {
      setTopUpLoading(false);
      showAlert('Error', 'Failed to initiate top-up');
    }
  };

  const getRemainingDays = () => {
    if (!currentSubscription?.endDate) return 0;
    const today = new Date();
    const endDate = new Date(currentSubscription.endDate);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const renderPlanCard = (plan, index) => {
    const isActive = currentSubscription?.subscription?._id === plan._id;
    const currentPlanIdx = subscriptionPlans.findIndex(p => p._id === currentSubscription?.subscription?._id);
    const isDowngrade = currentPlanIdx !== -1 && index < currentPlanIdx;
    const lowestTier = plan.pricingTiers.reduce((min, tier) => tier.price < min.price ? tier : min, plan.pricingTiers[0]);

    return (
      <Card key={plan._id} sx={{ mb: 2, borderRadius: 3, border: isActive ? '2px solid #851E41' : '1px solid #E5E7EB' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>
                {plan.name}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                {plan.description}
              </Typography>
            </Box>
            {isActive && (
              <Chip label="CURRENT" size="small" sx={{ bgcolor: '#F8E9EE', color: '#851E41', fontWeight: 600 }} />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#851E41' }}>
              {formatCurrency(lowestTier.price)}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#6B7280', ml: 1 }}>
              /{lowestTier.billingPeriod || 'month'}
            </Typography>
          </Box>

          <Box sx={{ bgcolor: '#F8E9EE', borderRadius: 2, p: 1.5, mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1F2937', mb: 1 }}>
              Available Order Tiers:
            </Typography>
            {plan.pricingTiers.map((tier, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                  {tier.minOrders}-{tier.maxOrders === 10000 ? 'Unlimited' : tier.maxOrders} orders
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1F2937' }}>
                  {formatCurrency(tier.price)}/{tier.billingPeriod || 'month'}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mb: 2 }}>
            {plan.features?.map((feature, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: '#10B981', mr: 1 }} />
                <Typography sx={{ fontSize: 13, color: '#1F2937' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>

          <Button
            fullWidth
            variant={isActive ? 'outlined' : 'contained'}
            onClick={() => !isDowngrade && handleSelectPlan(plan)}
            disabled={isDowngrade}
            sx={{
              bgcolor: isActive ? '#F8E9EE' : '#851E41',
              color: isActive ? '#851E41' : '#fff',
              borderColor: isActive ? '#851E41' : 'transparent',
              '&:hover': { bgcolor: isActive ? '#F5DCE4' : '#6B1830' },
              '&:disabled': { bgcolor: '#E5E7EB', color: '#6B7280' },
            }}
          >
            {isActive ? 'CURRENT PLAN' : isDowngrade ? 'DOWNGRADE NOT AVAILABLE' : 'SELECT PLAN'}
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader title="Subscription" />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress sx={{ color: '#851E41' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppHeader title="Subscription" />
      <AlertModal
        visible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        actions={alertActions}
        onRequestClose={() => setAlertVisible(false)}
      />

      <Box sx={{ p: 2 }}>
        <Box sx={{ bgcolor: '#851E41', borderRadius: 3, p: 3, mb: 2 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', mb: 1 }}>
            Restaurant Subscription
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>
            Manage your restaurant subscription
          </Typography>
        </Box>

        {currentSubscription && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>Current Plan</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>
                    {currentSubscription.subscription?.name || 'Loading...'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: currentSubscription.status === 'active' ? '#10B981' : '#EF4444',
                    }}
                  />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: currentSubscription.status === 'active' ? '#10B981' : '#EF4444' }}>
                    {currentSubscription.status === 'active' ? 'ACTIVE' : 'EXPIRED'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {currentSubscription && currentUsage && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937', mb: 2 }}>
                Your Subscription Usage
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Orders</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                    {currentUsage.orderCount} / {currentUsage.maxOrders}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(currentUsage.usagePercentage, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#F3F4F6',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: currentUsage.usagePercentage > 80 ? '#EF4444' : '#851E41',
                    },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 1 }}>
                  {currentUsage.remainingOrders} orders remaining
                </Typography>
              </Box>

              {getRemainingDays() > 0 && getRemainingDays() <= 1000 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Billing Period</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                      {getRemainingDays()} days left
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={100 - (getRemainingDays() / 30 * 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: '#F3F4F6',
                      '& .MuiLinearProgress-bar': { bgcolor: '#851E41' },
                    }}
                  />
                </Box>
              )}

              {currentUsage.usagePercentage > 80 && currentUsage.usagePercentage <= 100 && (
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#FEF3C7', p: 1.5, borderRadius: 2, mb: 2 }}>
                  <WarningIcon sx={{ fontSize: 20, color: '#92400E', mr: 1 }} />
                  <Typography sx={{ fontSize: 13, color: '#92400E' }}>
                    You're approaching your order limit. Excess orders will be charged at ₹10 per order.
                  </Typography>
                </Box>
              )}

              {currentUsage.usagePercentage > 100 && (
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#FEF3C7', p: 1.5, borderRadius: 2, mb: 2 }}>
                  <WarningIcon sx={{ fontSize: 20, color: '#92400E', mr: 1 }} />
                  <Typography sx={{ fontSize: 13, color: '#92400E' }}>
                    You've reached your order limit. Please top-up to continue accepting orders or orders will be charged at ₹10 per order.
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleTopUp}
                sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' } }}
              >
                TOP-UP ORDERS
              </Button>
            </CardContent>
          </Card>
        )}

        {!currentSubscription && (
          <Card sx={{ mb: 2, borderRadius: 3, textAlign: 'center', p: 3 }}>
            <InfoIcon sx={{ fontSize: 40, color: '#851E41', mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 1 }}>
              No Active Subscription
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
              You don't have an active subscription plan. Select one below to start accepting orders.
            </Typography>
          </Card>
        )}

        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937', mb: 2 }}>
          Available Plans
        </Typography>

        {subscriptionPlans.map((plan, index) => renderPlanCard(plan, index))}
      </Box>

      {/* Tier Selection Dialog */}
      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>Select Order Tier</Typography>
          <Button size="small" onClick={() => setTierDialogOpen(false)} sx={{ minWidth: 'auto', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: '#6B7280' }}>
            Choose the order volume tier for {selectedPlan?.name}
          </Typography>
          {paymentLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#851E41' }} />
              <Typography sx={{ ml: 2, color: '#6B7280' }}>Processing payment...</Typography>
            </Box>
          )}
          {selectedPlan?.pricingTiers?.map((tier, idx) => {
            const remaining = currentUsage?.remainingOrders || 0;
            const tierMax = tier.maxOrders === 10000 ? Number.MAX_SAFE_INTEGER : tier.maxOrders;
            const tierBlocked = remaining > tierMax;
            return (
              <Card
                key={idx}
                sx={{
                  mb: 1,
                  cursor: tierBlocked ? 'not-allowed' : 'pointer',
                  opacity: tierBlocked ? 0.5 : 1,
                  '&:hover': { bgcolor: tierBlocked ? 'transparent' : '#F3F4F6' },
                }}
                onClick={() => { if (!tierBlocked) handleTierSelection(tier); }}
              >
                <CardContent sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {tier.minOrders}-{tier.maxOrders === 10000 ? 'Unlimited' : tier.maxOrders} orders
                    </Typography>
                    {tierBlocked && (
                      <Typography sx={{ fontSize: 12, color: '#EF4444', mt: 0.5 }}>
                        Exceeds your remaining orders ({remaining})
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: '#851E41' }}>
                    {formatCurrency(tier.price)}/{tier.billingPeriod || 'month'}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Top Up Dialog */}
      <Dialog open={topUpDialogOpen} onClose={() => setTopUpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>Top Up Orders</Typography>
          <Button size="small" onClick={() => { setTopUpOrders(''); setTopUpError(''); setTopUpDialogOpen(false); }} sx={{ minWidth: 'auto', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: '#6B7280' }}>
            Add more orders to your current plan
          </Typography>
          <TextField
            fullWidth
            label="Number of Orders"
            value={topUpOrders}
            onChange={(e) => {
              setTopUpOrders(e.target.value.replace(/[^0-9]/g, ''));
              setTopUpError('');
            }}
            error={!!topUpError}
            helperText={topUpError}
            sx={{ mb: 2 }}
          />
          <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#6B7280' }}>Cost per order</Typography>
              <Typography sx={{ fontWeight: 500 }}>₹10</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#6B7280' }}>Orders</Typography>
              <Typography sx={{ fontWeight: 500 }}>{topUpOrders || '0'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #E5E7EB', pt: 1, mt: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>Total Amount</Typography>
              <Typography sx={{ fontWeight: 700, color: '#851E41' }}>
                ₹{((parseInt(topUpOrders, 10) || 0) * 10).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTopUpOrders(''); setTopUpError(''); setTopUpDialogOpen(false); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitTopUp}
            disabled={topUpLoading}
            sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' } }}
          >
            {topUpLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Top-up Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Breakdown Dialog */}
      <Dialog open={showUpgradeBreakdown} onClose={() => { setShowUpgradeBreakdown(false); upgradeConfirmRef.current?.reject?.(new Error('User cancelled')); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>Upgrade Cost Breakdown</Typography>
          <Button size="small" onClick={() => { setShowUpgradeBreakdown(false); upgradeConfirmRef.current?.reject?.(new Error('User cancelled')); }} sx={{ minWidth: 'auto', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: '#6B7280' }}>Upgrade Cost Breakdown:</Typography>
          {upgradeBreakdown && (
            <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', mb: 0.5 }}>Current Plan</Typography>
                <Typography sx={{ fontSize: 14 }}>{upgradeBreakdown.currentPlanName}</Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>Remaining Orders: {upgradeBreakdown.remainingOrders}</Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>Credit Value: {formatCurrency(upgradeBreakdown.creditValue)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', mb: 0.5 }}>New Plan</Typography>
                <Typography sx={{ fontSize: 14 }}>{upgradeBreakdown.newPlanName}</Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>Original Cost: {formatCurrency(upgradeBreakdown.originalCost)}</Typography>
              </Box>
              <Box sx={{ borderTop: '1px solid #E5E7EB', pt: 1 }}>
                <Typography sx={{ fontSize: 13 }}>Credit Applied: {formatCurrency(upgradeBreakdown.creditValue)}</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#851E41', mt: 0.5 }}>Final Amount to Pay: {formatCurrency(upgradeBreakdown.finalAmount)}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowUpgradeBreakdown(false); upgradeConfirmRef.current?.reject?.(new Error('User cancelled')); }}>Cancel</Button>
          <Button variant="contained" onClick={() => { setShowUpgradeBreakdown(false); upgradeConfirmRef.current?.resolve?.(); }} sx={{ bgcolor: '#851E41' }}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Confirm Dialog */}
      <Dialog open={showPaymentConfirm} onClose={() => { setShowPaymentConfirm(false); paymentConfirmRef.current?.reject?.(new Error('User cancelled')); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>Confirm Payment</Typography>
          <Button size="small" onClick={() => { setShowPaymentConfirm(false); paymentConfirmRef.current?.reject?.(new Error('User cancelled')); }} sx={{ minWidth: 'auto', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: '#6B7280' }}>
            You are about to pay {formatCurrency(paymentConfirmData.amount)} for {paymentConfirmData.description}. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowPaymentConfirm(false); paymentConfirmRef.current?.reject?.(new Error('User cancelled')); }}>Cancel</Button>
          <Button variant="contained" onClick={() => { setShowPaymentConfirm(false); paymentConfirmRef.current?.resolve?.(); }} sx={{ bgcolor: '#851E41' }}>
            PAY NOW
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPage;
